import { PERMISSIONS } from './permissions';
import type { PermissionCode } from './permissions';

export interface RoutePolicy {
  id: string;
  path: string;
  routePermission: PermissionCode;
  featurePermissions: readonly PermissionCode[];
}

export const ROUTE_POLICIES = [
  {
    id: 'dashboard',
    path: '/inicio',
    routePermission: PERMISSIONS.UI.DASHBOARD,
    featurePermissions: [PERMISSIONS.REPORTING.DASHBOARD.READ],
  },
  {
    id: 'checkout',
    path: '/caja',
    routePermission: PERMISSIONS.UI.CHECKOUT,
    featurePermissions: [PERMISSIONS.CATALOG.PRODUCTS.READ, PERMISSIONS.SALES.REGISTER],
  },
  {
    id: 'sales-history',
    path: '/ventas/historial',
    routePermission: PERMISSIONS.UI.SALES_HISTORY,
    featurePermissions: [PERMISSIONS.SALES.READ],
  },
  {
    id: 'products-catalog',
    path: '/productos/catalogo',
    routePermission: PERMISSIONS.UI.PRODUCTS_CATALOG,
    featurePermissions: [PERMISSIONS.CATALOG.PRODUCTS.READ],
  },
  {
    id: 'inventory-lots',
    path: '/inventario/lotes',
    routePermission: PERMISSIONS.UI.INVENTORY_LOTS,
    featurePermissions: [
      PERMISSIONS.CATALOG.PRODUCTS.READ,
      PERMISSIONS.INVENTORY.LOTS.READ,
      PERMISSIONS.INVENTORY.MOVEMENTS.READ,
    ],
  },
  {
    id: 'reports-stock',
    path: '/reportes/stock',
    routePermission: PERMISSIONS.UI.REPORTS_STOCK,
    featurePermissions: [PERMISSIONS.REPORTING.STOCK.READ],
  },
  {
    id: 'users',
    path: '/administracion/usuarios',
    routePermission: PERMISSIONS.UI.USERS,
    featurePermissions: [PERMISSIONS.IDENTITY.USERS.READ],
  },
  {
    id: 'roles-permissions',
    path: '/administracion/roles-permisos',
    routePermission: PERMISSIONS.UI.ROLES_PERMISSIONS,
    featurePermissions: [PERMISSIONS.IDENTITY.ROLES.READ],
  },
] as const satisfies readonly RoutePolicy[];

export type RoutePolicyId = (typeof ROUTE_POLICIES)[number]['id'];

export function getRoutePolicyByPath(path: string): RoutePolicy | undefined {
  return ROUTE_POLICIES.find((policy) => policy.path === path);
}

export function getRoutePolicyById(id: RoutePolicyId): RoutePolicy {
  const policy = ROUTE_POLICIES.find((entry) => entry.id === id);
  if (!policy) {
    throw new Error(`Route policy "${id}" is not defined.`);
  }
  return policy;
}

export function getAllRequiredPermissions(policy: RoutePolicy): readonly string[] {
  return [policy.routePermission, ...policy.featurePermissions];
}

export function hasAllRequiredPermissions(
  userPermissions: readonly string[],
  requiredPermissions: readonly string[],
): boolean {
  if (requiredPermissions.length === 0) {
    return true;
  }
  const granted = new Set(userPermissions);
  return requiredPermissions.every((permission) => granted.has(permission));
}
